export const parseOrderDate = (dateStr) => {
  if (!dateStr) return null;
  if (dateStr instanceof Date) return isNaN(dateStr.getTime()) ? null : dateStr;
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return new Date(`${s.slice(0, 10)}T00:00:00`);
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

export const formatOrderDate = (dateStr) => {
  const d = parseOrderDate(dateStr);
  if (!d) return dateStr || '—';
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const formatOrderTime = (timeStr) => {
  if (!timeStr) return '—';
  const s = String(timeStr).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return s.slice(0, 8);
  const d = new Date(s);
  if (!isNaN(d.getTime())) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  }
  return s;
};

export const normalizeOrderDateKey = (dateStr) => {
  const d = parseOrderDate(dateStr);
  if (!d) return '';
  return d.toISOString().slice(0, 10);
};

export const formatMonthHeading = (dateStr) => {
  const d = parseOrderDate(dateStr);
  if (!d) return 'Unknown';
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export const getMonthKey = (dateStr) => {
  const d = parseOrderDate(dateStr);
  if (!d) return 'unknown';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const groupOrdersByMonth = (orders) => {
  const groups = {};
  orders.forEach((order) => {
    const key = getMonthKey(order.date);
    if (!groups[key]) {
      groups[key] = { key, label: formatMonthHeading(order.date), orders: [] };
    }
    groups[key].orders.push(order);
  });
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
};

export const filterOrders = (orders, { search, dateFilter, customFrom, customTo }) => {
  let result = [...orders];
  const q = search?.trim().toLowerCase();

  if (q) {
    result = result.filter(
      (o) =>
        String(o.orderId).includes(q) ||
        o.customerName?.toLowerCase().includes(q) ||
        o.customerEmail?.toLowerCase().includes(q) ||
        o.customerPhone?.includes(q)
    );
  }

  if (dateFilter) {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yStr = yesterday.toISOString().split('T')[0];

    const daysAgo = (n) => {
      const d = new Date(now);
      d.setDate(d.getDate() - n);
      return d.toISOString().split('T')[0];
    };

    const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastMonthStart = lastMonth.toISOString().split('T')[0];
    const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().split('T')[0];

    const filters = {
      today: (d) => d === today,
      yesterday: (d) => d === yStr,
      last7: (d) => d >= daysAgo(7),
      last30: (d) => d >= daysAgo(30),
      thisMonth: (d) => d >= monthStart,
      lastMonth: (d) => d >= lastMonthStart && d <= lastMonthEnd,
      custom: (d) => (!customFrom || d >= customFrom) && (!customTo || d <= customTo),
    };

    const fn = filters[dateFilter];
    if (fn) result = result.filter((o) => fn(normalizeOrderDateKey(o.date)));
  }

  return result;
};

export const sortOrders = (orders, sortBy) => {
  const list = [...orders];
  switch (sortBy) {
    case 'oldest':
      return list.sort((a, b) => a.orderId - b.orderId);
    case 'highest':
      return list.sort((a, b) => b.orderTotal - a.orderTotal);
    case 'lowest':
      return list.sort((a, b) => a.orderTotal - b.orderTotal);
    default:
      return list.sort((a, b) => b.orderId - a.orderId);
  }
};

export const filterOrdersByDateRange = (orders, from, to) => {
  if (!from && !to) return [...orders];
  return orders.filter((o) => {
    const d = normalizeOrderDateKey(o.date);
    if (from && d < from) return false;
    if (to && d > to) return false;
    return true;
  });
};

export const getWeeklyOrders = (orders) => {
  const now = new Date();
  return orders.filter((o) => {
    const d = new Date(o.date);
    return (now - d) / (1000 * 60 * 60 * 24) <= 7;
  });
};

export const getMonthlyOrders = (orders, monthLabel = new Date().toISOString().slice(0, 7)) =>
  orders.filter((o) => getMonthKey(o.date) === monthLabel);
